/**
 * Privacy-safe PostHog bootstrap for the Perfect Predict public website only.
 * SDK is dynamically imported — never during prerender, and not until needed.
 */
import { getAnalyticsConsent, onAnalyticsConsentChange, setAnalyticsConsent } from './consent'
import {
  isPublicEventName,
  isTrackablePublicPath,
  normalizePath,
  sanitizeEventProps,
  type PublicEventName,
  type PublicEventProps,
} from './events'
import { captureAndPersistUtms, loadPersistedUtms } from './utm'

type PostHogClient = typeof import('posthog-js/dist/module.full.no-external').default

let posthog: PostHogClient | null = null
let initialized = false
let initPromise: Promise<boolean> | null = null
let lastPageviewPath: string | null = null
let consentUnsub: (() => void) | null = null

function envKey(): string | undefined {
  return (import.meta.env.VITE_POSTHOG_KEY as string | undefined)?.trim() || undefined
}

function envHost(): string {
  return (
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined)?.trim() || 'https://us.i.posthog.com'
  )
}

/** True when we must not run analytics (SSR, prerender, tests, missing config). */
export function shouldSkipPostHogInit(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return true
  if (typeof navigator !== 'undefined' && navigator.webdriver) return true
  if (import.meta.env.MODE === 'test') return true
  if ((window as Window & { __PP_PRERENDER__?: boolean }).__PP_PRERENDER__) return true
  const debug = (import.meta.env.VITE_POSTHOG_DEBUG as string | undefined) === '1'
  if (!import.meta.env.PROD && !debug) return true
  if (!envKey()) return true
  return false
}

function replayAllowedForPath(path: string): boolean {
  const p = normalizePath(path)
  return p !== '/login' && p !== '/register' && p !== '/web-test'
}

function applyConsentState(consent: ReturnType<typeof getAnalyticsConsent>) {
  if (!initialized || !posthog) return
  if (consent === 'accepted') {
    posthog.opt_in_capturing()
    if (replayAllowedForPath(window.location.pathname)) {
      posthog.startSessionRecording()
    } else {
      posthog.stopSessionRecording()
    }
  } else {
    posthog.stopSessionRecording()
    posthog.opt_out_capturing()
    try {
      posthog.reset(true)
    } catch {
      /* ignore */
    }
  }
}

async function loadSdk(): Promise<PostHogClient | null> {
  if (posthog) return posthog
  const mod = await import('posthog-js/dist/module.full.no-external')
  posthog = mod.default
  return posthog
}

/**
 * Initialize PostHog once (lazy). Safe under React StrictMode.
 * Loads the SDK only when configuration allows; remains opted-out until consent.
 */
export function initPostHog(): Promise<boolean> {
  if (initialized) return Promise.resolve(true)
  if (shouldSkipPostHogInit()) return Promise.resolve(false)
  if (initPromise) return initPromise

  initPromise = (async () => {
    if (initialized) return true
    if (shouldSkipPostHogInit()) return false
    const key = envKey()
    if (!key) return false

    const ph = await loadSdk()
    if (!ph) return false
    if (initialized) return true

    ph.init(key, {
      api_host: envHost(),
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: false,
      autocapture: false,
      disable_session_recording: true,
      opt_out_capturing_by_default: true,
      persistence: 'localStorage+cookie',
      respect_dnt: true,
      session_recording: {
        maskAllInputs: true,
        maskInputOptions: {
          password: true,
        },
        maskTextSelector: 'input, textarea, select, .ph-mask',
        blockSelector: '.ph-no-capture',
        recordCrossOriginIframes: false,
        collectFonts: false,
      },
    })

    initialized = true
    captureAndPersistUtms()
    applyConsentState(getAnalyticsConsent())

    if (!consentUnsub) {
      consentUnsub = onAnalyticsConsentChange((v) => {
        applyConsentState(v === 'accepted' ? 'accepted' : v === 'rejected' ? 'rejected' : null)
      })
    }
    return true
  })()

  return initPromise
}

export function isPostHogInitialized(): boolean {
  return initialized
}

export function getPostHogForTests() {
  return posthog
}

/** Reset module state — tests only. */
export function __resetPostHogForTests(): void {
  initialized = false
  initPromise = null
  posthog = null
  lastPageviewPath = null
  if (consentUnsub) {
    consentUnsub()
    consentUnsub = null
  }
}

export function sanitizeUrlForAnalytics(href: string): string {
  try {
    const u = new URL(href, 'https://www.perfectpredict.com')
    return `${u.origin}${normalizePath(u.pathname)}`
  } catch {
    return normalizePath(href)
  }
}

export function capturePageview(pathname?: string): void {
  if (!initialized || !posthog) return
  if (getAnalyticsConsent() !== 'accepted') return

  const path = normalizePath(pathname || window.location.pathname)
  if (!isTrackablePublicPath(path)) {
    posthog.stopSessionRecording()
    return
  }
  if (lastPageviewPath === path) return
  lastPageviewPath = path

  if (replayAllowedForPath(path)) {
    posthog.startSessionRecording()
  } else {
    posthog.stopSessionRecording()
  }

  posthog.capture('$pageview', {
    $pathname: path,
    page_path: path,
    ...loadPersistedUtms(),
  })
}

export function trackPublicEvent(name: PublicEventName | string, props?: PublicEventProps): void {
  if (!initialized || !posthog) return
  if (
    getAnalyticsConsent() !== 'accepted' &&
    name !== 'cookie_consent_accepted' &&
    name !== 'cookie_consent_rejected'
  ) {
    return
  }
  if (!isPublicEventName(name)) return
  const clean = sanitizeEventProps({
    page_path: normalizePath(typeof window !== 'undefined' ? window.location.pathname : '/'),
    ...loadPersistedUtms(),
    ...props,
  })
  posthog.capture(name, clean)
}

export async function acceptAnalyticsConsent(): Promise<void> {
  setAnalyticsConsent('accepted')
  await initPostHog()
  // Meta Pixel shares analytics consent with PostHog (static import via track/WebsiteAnalytics).
  try {
    const meta = await import('./meta-pixel')
    meta.initMetaPixel()
    meta.trackMetaPageView()
  } catch {
    /* Meta Pixel optional */
  }
  applyConsentState('accepted')
  if (initialized && posthog) {
    posthog.capture(
      'cookie_consent_accepted',
      sanitizeEventProps({
        cta_location: 'banner',
        page_path: normalizePath(window.location.pathname),
        ...loadPersistedUtms(),
      }),
    )
  }
  capturePageview()
}

export async function rejectAnalyticsConsent(): Promise<void> {
  setAnalyticsConsent('rejected')
  // Only load SDK if it was already initialized; otherwise stay fully unloaded.
  if (initialized && posthog) {
    posthog.opt_in_capturing()
    posthog.capture(
      'cookie_consent_rejected',
      sanitizeEventProps({
        cta_location: 'banner',
        page_path: normalizePath(window.location.pathname),
      }),
    )
    applyConsentState('rejected')
  }
}
